import moment from 'moment-timezone';
import { Routes } from 'src/app/common/constants';
import {
  ConfigurationErrors,
  CategoryEnum,
  DisplayFormatEnum,
  KeyTaskConfiguration,
  KeyTaskConfigurationForSave,
  KeyTaskRecord,
  OpenByTypeEnum,
  PublishedStatusEnum,
  RedirectTypeEnum,
} from '../types/key-task-summarizer-type';

export const categories: CategoryEnum[] = Object.values(CategoryEnum);

export const getCategoryTranslationKey = (category: CategoryEnum): string => {
  const translationKeys: Record<CategoryEnum, string> = {
    [CategoryEnum.CUSTOMER_OFFER]: 'KeyTaskSummerizer.category_customer_offer',
    [CategoryEnum.TIMETABLE]: 'KeyTaskSummerizer.category_timetable',
    [CategoryEnum.MANAGEMENT_MESSAGE]: 'KeyTaskSummerizer.category_management_message',
  };
  return translationKeys[category];
};

export const createConfiguration = (): KeyTaskConfiguration => ({
  category: CategoryEnum.CUSTOMER_OFFER,
  contentEn: '',
  contentZhHk: '',
  order: undefined,
  effectiveStartDate: null,
  effectiveEndDate: null,
  publishedStatus: '',

  // Selected campaign ID for Customer Offer Category
  selectedCampaign: null,

  // Countdown date and display format for Timetable Category
  countdownDate: null,
  displayFormat: '',

  // Redirect and link related fields for Management Messages Category
  redirectTo: '',
  link: '',
  params: {},
  openBy: '',
  canShare: '',
});

export const validateConfiguration = (value: KeyTaskConfiguration): ConfigurationErrors => {
  const errors: ConfigurationErrors = {};
  const required = 'component.form-required-text';
  const startDate = moment(value.effectiveStartDate);
  const endDate = moment(value.effectiveEndDate);

  if (!categories.includes(value.category)) errors.category = required;
  if (!value.contentEn.trim()) errors.contentEn = required;
  if (!value.contentZhHk.trim()) errors.contentZhHk = required;
  if (!Number.isSafeInteger(Number(value.order)) || Number(value.order) < 0) {
    errors.order = 'KeyTaskSummerizer.error_order';
  }
  if (!value.effectiveStartDate || !startDate.isValid()) errors.effectiveStartDate = required;
  if (!value.effectiveEndDate || !endDate.isValid()) errors.effectiveEndDate = required;

  if (startDate.isValid() && endDate.isValid() && endDate.isBefore(startDate)) {
    errors.effectiveEndDate = 'component.form-date-compare';
  }

  switch (value.category) {
    case CategoryEnum.CUSTOMER_OFFER:
      if (!value.selectedCampaign) errors.selectedCampaign = required;
      break;

    case CategoryEnum.TIMETABLE: {
      const countdownDate = moment(value.countdownDate);
      if (!value.countdownDate || !countdownDate.isValid()) {
        errors.countdownDate = required;
      } else if (startDate.isValid() && countdownDate.isBefore(startDate, 'day')) {
        errors.countdownDate = 'KeyTaskSummerizer.error_countdown';
      }
      if (
        value.displayFormat !== DisplayFormatEnum.DISPLAY_DATE &&
        value.displayFormat !== DisplayFormatEnum.COUNTDOWN_DAYS
      ) {
        errors.displayFormat = required;
      }
      break;
    }

    case CategoryEnum.MANAGEMENT_MESSAGE: {
      const { redirectTo, params, openBy, canShare } = value;
      const link = value.link?.trim();
      if (!redirectTo || !Object.values(RedirectTypeEnum).includes(redirectTo)) {
        errors.redirectTo = required;
      }
      if (redirectTo === RedirectTypeEnum.NO) break;
      if (!link) errors.link = required;

      if (redirectTo === RedirectTypeEnum.APP) {
        const isSpecificLink =
          link === Routes.SPECIFIC_EVENT_LINK ||
          link === Routes.SPECIFIC_TRAINING_LINK ||
          link === Routes.SPECIFIC_SURVEY_LINK ||
          link === Routes.SPECIFIC_CAMPAIGN_LINK ||
          link === Routes.NEWS_DETAIL;
        const linkParams = params?.linkParams;
        if (isSpecificLink && !linkParams?.id && !linkParams?.eventId && !linkParams?.campaignId && !linkParams?.newsId) {
          errors.params = required;
        }
      }

      if (redirectTo === RedirectTypeEnum.EXTERNAL && link) {
        try {
          const url = new URL(link);
          if (!['http:', 'https:'].includes(url.protocol)) errors.link = 'component.form-link-text';
        } catch {
          errors.link = 'component.form-link-text';
        }
        if (openBy !== OpenByTypeEnum.WEBVIEW && openBy !== OpenByTypeEnum.BROWSER) {
          errors.openBy = required;
        }
        if (openBy === OpenByTypeEnum.WEBVIEW && typeof canShare !== 'boolean') {
          errors.canShare = required;
        }
      }
      break;
    }
  }
  return errors;
};

export const isRecordValid = (record: KeyTaskRecord): boolean => {
  const now = moment();
  const startDate = record.effectiveStartDate ? moment(record.effectiveStartDate) : null;
  const endDate = record.effectiveEndDate ? moment(record.effectiveEndDate) : null;
  return (
    record.publishedStatus === PublishedStatusEnum.PUBLISHED &&
    (!startDate || (startDate.isValid() && startDate.isSameOrBefore(now))) &&
    (!endDate || (endDate.isValid() && endDate.isSameOrAfter(now)))
  );
};

export const configurationForSave = ({
  category,
  contentEn,
  contentZhHk,
  order,
  effectiveStartDate,
  effectiveEndDate,
  publishedStatus,
  selectedCampaign,
  countdownDate,
  displayFormat,
  redirectTo,
  params,
  openBy,
  canShare,
  link,
}: KeyTaskConfiguration): KeyTaskConfigurationForSave => {
  const base = {
    category,
    order: Number(order),
    contentEn: contentEn.trim(),
    contentZhHk: contentZhHk.trim(),
    effectiveStartDate,
    effectiveEndDate,
    publishedStatus,
  };

  switch (category) {
    case CategoryEnum.CUSTOMER_OFFER:
      return {
        ...base,
        selectedCampaignId: selectedCampaign?._id,
      };

    case CategoryEnum.TIMETABLE:
      return {
        ...base,
        countdownDate,
        displayFormat,
      };

    case CategoryEnum.MANAGEMENT_MESSAGE: {
      if (redirectTo === RedirectTypeEnum.NO) {
        return {
          ...base,
          redirectTo,
        };
      }

      if (redirectTo === RedirectTypeEnum.APP) {
        return {
          ...base,
          redirectTo,
          link: link?.trim(),
          params,
        };
      }

      return {
        ...base,
        redirectTo,
        link: link?.trim(),
        openBy,
        ...(openBy === OpenByTypeEnum.WEBVIEW && {
          canShare,
        }),
      };
    }

    default:
      return base;
  }
};
